import { FC, useCallback, useState } from "react";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Chip,
  Flex,
  Group,
  Loader,
  Space,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";

import {
  FindingTriageReason,
  FindingTriageState,
} from "@protos/common/v1/common";
import { IssuesFilter, IssueType } from "@protos/issues/v1/issues";
import {
  FindingsFilters,
  TRIAGE_REASONS,
  TriageReason,
  TriageState,
} from "@shared/types";

import { useTriage } from "../../../hooks";
import { useSelectAllFindings } from "../../../stores";
import {
  IgnoreFutureFindingsOptions,
  IgnoreFutureFindingsOptionsProps,
} from "../../ImprovedTriageOptions/IgnoreFutureFindingsOptions";
import { TriageToOptions } from "../types/triageToOptions";

interface IgnoreFormProps {
  issueType: IssueType;
  findingIds?: number[];
  triageFilter: IssuesFilter | null;
  issueParentIds: number[];
  ruleIds: number[];
  currentTriageState: TriageState;
  refetchAfterTriage?: boolean;
  ignoreOptions?: IgnoreFutureFindingsOptionsProps;
  isLoadingIgnoreOptions?: boolean;
  onSaveClick?: () => void;
  filterOverrides?: FindingsFilters;
  disableBack?: boolean;
  onBack: () => void;
}

const triageReasonProtoMap: { [key in TriageReason]: FindingTriageReason } = {
  false_positive: FindingTriageReason.FALSE_POSITIVE,
  acceptable_risk: FindingTriageReason.ACCEPTABLE_RISK,
  no_time: FindingTriageReason.NO_TIME,
};

export const IgnoreForm: FC<IgnoreFormProps> = ({
  issueType,
  findingIds,
  triageFilter,
  issueParentIds,
  ruleIds,
  currentTriageState,
  refetchAfterTriage = true,
  ignoreOptions,
  isLoadingIgnoreOptions,
  onSaveClick = () => {},
  filterOverrides = {},
  disableBack = false,
  onBack,
}) => {
  const { bulkTriage, bulkTriageFindingsV2, bulkTriageSecrets, triageFinding } =
    useTriage(issueType, refetchAfterTriage);
  const selectAllFindings = useSelectAllFindings();
  const [note, setNote] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const action =
    currentTriageState === "ignored"
      ? ({
          label: "Reopen",
          value: "reopened",
          protoValue: FindingTriageState.REOPENED,
          placeholderText: "Why is this being reopened?",
        } as const)
      : ({
          label: "Ignore",
          value: "ignored",
          protoValue: FindingTriageState.IGNORED,
          placeholderText: "Comment on ignore reason...",
        } as const);
  const [triageReason, setTriageReason] = useState<TriageReason>();
  const [triageReasonProto, setTriageReasonProto] =
    useState<FindingTriageReason>();

  const triageReasonsProto = (): [FindingTriageReason, string][] => {
    return [
      [FindingTriageReason.ACCEPTABLE_RISK, "Acceptable risk"],
      [FindingTriageReason.FALSE_POSITIVE, "False positive"],
      [FindingTriageReason.NO_TIME, "No time"],
    ];
  };

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    if (issueType === IssueType.SECRETS) {
      // Note: This differs from SAST since we use bulk triaging even for one finding (> 0)
      if (findingIds === undefined || findingIds.length > 0) {
        await bulkTriageSecrets({
          triageState: action.protoValue,
          triageReason: triageReasonProto,
          note,
          ids: findingIds || [],
          filterOverrides,
        });
      }

      setIsLoading(false);
      onSaveClick();
      setNote("");
      return;
    }

    let triageState: TriageToOptions = null;
    if (action.value === "reopened" || action.value === "ignored") {
      triageState = action.value as TriageToOptions;
    }

    if (triageFilter) {
      if (selectAllFindings) {
        // If select all, do not merge rule ids or issue ids
        await bulkTriageFindingsV2({
          filter: triageFilter,
          triageState: action.protoValue,
          triageReason: triageReason && triageReasonProtoMap[triageReason],
          note,
        });
      } else {
        // Triaging together would filter by issueParentIds AND ruleIds
        // resulting in no rows, so perform separate requests.
        if (issueParentIds.length > 0) {
          await bulkTriageFindingsV2({
            filter: {
              ...triageFilter,
              issueParentIds: issueParentIds,
            },
            triageState: action.protoValue,
            triageReason: triageReason && triageReasonProtoMap[triageReason],
            note,
          });
        }
        if (ruleIds.length > 0) {
          await bulkTriageFindingsV2({
            filter: {
              ...triageFilter,
              ruleIds,
            },
            triageState: action.protoValue,
            triageReason: triageReason && triageReasonProtoMap[triageReason],
            note,
          });
        }
      }

      setIsLoading(false);
      onSaveClick();
      setNote("");
      return;
    }

    // Triage findings- success/failure notifications are handled in hook
    if (
      findingIds === undefined ||
      findingIds.length > 1 ||
      filterOverrides.rule_id !== undefined
    ) {
      // triaging everything in the current set of filters
      await bulkTriage(
        triageState,
        note,
        triageReason,
        findingIds,
        filterOverrides
      );
    } else {
      // triaging just one finding
      await triageFinding(
        findingIds[0],
        triageState,
        note,
        undefined,
        triageReason
      );
    }

    setIsLoading(false);
    // should close the popover
    onSaveClick();
    setNote("");
  }, [
    issueType,
    action.value,
    action.protoValue,
    findingIds,
    filterOverrides,
    onSaveClick,
    bulkTriageSecrets,
    triageReasonProto,
    note,
    bulkTriage,
    triageReason,
    triageFinding,
    bulkTriageFindingsV2,
    triageFilter,
    issueParentIds,
    ruleIds,
    selectAllFindings,
  ]);

  if (currentTriageState === "ignored") {
    return (
      <Stack gap={8}>
        <Textarea
          data-testid="IgnoreFormNotes"
          placeholder={action.placeholderText}
          data-autofocus
          autosize
          minRows={2}
          maxRows={20}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Flex justify="end">
          <Button
            data-testid="IgnoreFormSubmitButton"
            disabled={note === ""}
            size="xs"
            onClick={handleSave}
            loading={isLoading}
          >
            {action.label}
          </Button>
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack gap={8}>
      <Text size="xs">Ignore reason</Text>
      {issueType === IssueType.SECRETS ? (
        <Chip.Group
          value={triageReasonProto}
          onChange={(r) => setTriageReasonProto(r as FindingTriageReason)}
        >
          <Group gap={8}>
            {triageReasonsProto().map(([reason, text]) => (
              <Chip
                key={reason}
                value={reason}
                size="xs"
                data-testid="IgnoreReasonChip"
              >
                {text}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      ) : (
        <Chip.Group
          value={triageReason}
          onChange={(value) => setTriageReason(value as TriageReason)}
        >
          <Group gap={8}>
            {Object.entries(TRIAGE_REASONS).map(([reason, text]) => (
              <Chip
                key={reason}
                value={reason}
                size="xs"
                data-testid="IgnoreReasonChip"
              >
                {text}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      )}
      <Textarea
        data-testid="IgnoreFormNotes"
        placeholder={action.placeholderText}
        data-autofocus
        autosize
        minRows={2}
        maxRows={20}
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
      />
      {ignoreOptions && action.value === "ignored" ? (
        <IgnoreFutureFindingsOptions {...ignoreOptions} />
      ) : (
        isLoadingIgnoreOptions && <Loader />
      )}
      <Flex justify="space-between">
        {!disableBack ? (
          <Button onClick={onBack} size="xs" variant="subtle" c="dimmed">
            <FontAwesomeIcon icon={faArrowLeft} />
            <Space w={5} /> Back
          </Button>
        ) : (
          <div />
        )}
        <Button
          data-testid="IgnoreFormSubmitButton"
          size="xs"
          onClick={handleSave}
          loading={isLoading}
        >
          {action.label}
        </Button>
      </Flex>
    </Stack>
  );
};
